import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { wsClient } from '../services/websocket-client.js';
import type { InboxItem } from '../components/inbox/cp-inbox-item-card.js';
import '../components/inbox/cp-inbox-item-card.js';
import '../components/inbox/cp-inbox-edit-modal.js';
import '../components/shared/cp-page-subnav.js';

const COMM_TABS = [
  { label: 'CEO Chats', path: '/ceo-chats' },
  { label: 'Nachrichten', path: '/messages' },
  { label: 'Inbox', path: '/inbox' },
  { label: 'Kontakte', path: '/people' },
];

type InboxFilter = 'all' | 'pending' | 'accepted' | 'rejected';

const FILTER_LABELS: Record<InboxFilter, string> = {
  all: 'Alle',
  pending: 'Ausstehend',
  accepted: 'Angenommen',
  rejected: 'Abgelehnt',
};

const PAGE_SIZE = 20;

@customElement('cp-page-inbox')
export class CpPageInbox extends LitElement {
  createRenderRoot() { return this; }

  @state() private _items: InboxItem[] = [];
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _filter: InboxFilter = 'pending';
  @state() private _page = 1;
  @state() private _hasMore = false;
  @state() private _editModalOpen = false;
  @state() private _editItem: InboxItem | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    void this._loadItems();
    wsClient.addEventListener('ws-message', this._onWsMessage);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._onWsMessage);
  }

  private _onWsMessage = (e: Event): void => {
    const msg = (e as CustomEvent).detail as { type: string; payload: unknown };
    if (msg.type === 'inbox:item-received') {
      const newItem = msg.payload as InboxItem;
      if (this._filter === 'pending' || this._filter === 'all') {
        this._items = [newItem, ...this._items];
      }
    }
  };

  private async _loadItems(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      const params = new URLSearchParams();
      if (this._filter !== 'all') params.set('status', this._filter);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((this._page - 1) * PAGE_SIZE));
      const res = await fetch(`/api/inbox?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Fehler beim Laden: ${res.status}`);
      const items = await res.json() as InboxItem[];
      this._items = items;
      this._hasMore = items.length === PAGE_SIZE;
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Unbekannter Fehler';
    } finally {
      this._loading = false;
    }
  }

  private _setFilter(filter: InboxFilter): void {
    if (this._filter === filter) return;
    this._filter = filter;
    this._page = 1;
    void this._loadItems();
  }

  private _prevPage(): void {
    if (this._page <= 1) return;
    this._page -= 1;
    void this._loadItems();
  }

  private _nextPage(): void {
    if (!this._hasMore) return;
    this._page += 1;
    void this._loadItems();
  }

  private _onItemAccepted(e: Event): void {
    const { itemId } = (e as CustomEvent).detail as { itemId: number };
    this._items = this._items.filter(i => i.id !== itemId);
  }

  private _onItemRejected(e: Event): void {
    const { itemId } = (e as CustomEvent).detail as { itemId: number };
    this._items = this._items.filter(i => i.id !== itemId);
  }

  private _onItemEditRequest(e: Event): void {
    const { item } = (e as CustomEvent).detail as { item: InboxItem };
    this._editItem = item;
    this._editModalOpen = true;
  }

  private _onItemAcceptedWithEdits(e: Event): void {
    const { item } = (e as CustomEvent).detail as { item: InboxItem };
    this._items = this._items.filter(i => i.id !== item.id);
    this._editModalOpen = false;
    this._editItem = null;
  }

  private _onModalClosed(): void {
    this._editModalOpen = false;
    this._editItem = null;
  }

  render() {
    return html`
      <div
        class="cp-page-inbox"
        @item-accepted=${this._onItemAccepted}
        @item-rejected=${this._onItemRejected}
        @item-edit-request=${this._onItemEditRequest}
        @item-accepted-with-edits=${this._onItemAcceptedWithEdits}
        @modal-closed=${this._onModalClosed}
      >
        <cp-page-subnav .tabs=${COMM_TABS} activePath="/inbox"></cp-page-subnav>
        <div class="cp-page-inbox__header">
          <h1 class="cp-page-inbox__title">Inbox</h1>
        </div>

        <div class="cp-page-inbox__filters">
          ${(['all', 'pending', 'accepted', 'rejected'] as InboxFilter[]).map(f => html`
            <button
              class="cp-page-inbox__filter${this._filter === f ? ' cp-page-inbox__filter--active' : ''}"
              @click=${() => this._setFilter(f)}
            >${FILTER_LABELS[f]}</button>
          `)}
        </div>

        ${this._loading ? html`
          <div class="cp-page-inbox__loading">Lade Items…</div>
        ` : this._error ? html`
          <div class="cp-page-inbox__error">${this._error}</div>
        ` : this._items.length === 0 ? html`
          <div class="cp-page-inbox__empty">
            <p class="cp-page-inbox__empty-text">Keine neuen Vorschlaege</p>
          </div>
        ` : html`
          <div class="cp-page-inbox__list">
            ${this._items.map(item => html`
              <cp-inbox-item-card .item=${item}></cp-inbox-item-card>
            `)}
          </div>
          ${this._page > 1 || this._hasMore ? html`
            <div class="cp-page-inbox__pagination">
              <button
                class="cp-page-inbox__pagination-btn"
                ?disabled=${this._page <= 1}
                @click=${this._prevPage}
              >&#8592; Vorherige</button>
              <span class="cp-page-inbox__pagination-page">Seite ${this._page}</span>
              <button
                class="cp-page-inbox__pagination-btn"
                ?disabled=${!this._hasMore}
                @click=${this._nextPage}
              >Naechste &#8594;</button>
            </div>
          ` : nothing}
        `}

        <cp-inbox-edit-modal
          .item=${this._editItem}
          .open=${this._editModalOpen}
        ></cp-inbox-edit-modal>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-page-inbox': CpPageInbox;
  }
}
